import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Nav } from '../components/Nav';
import { Footer } from '../components/Footer';
import { CheckSquare, Users, Clock, Bell, BarChart, ListTodo, MessageSquare, Database, AlertTriangle, HelpCircle, UserX, CheckCircle2, XCircle, ArrowRight, Plug, Gift, Sparkles, Bot, Inbox, Workflow, PieChart, Calendar, ShieldCheck, Network, Repeat, Eye, Layout } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const painPoints = [{
  icon: <UserX size={28} data-id="element-2003" data-mp="1iys4oxezozgp" />,
  title: 'Managing the Manager',
  desc: 'You hired a project manager but still have to check if THEY did their job. You spend more time managing them than actually growing your business.'
}, {
  icon: <HelpCircle size={28} data-id="element-2004" data-mp="plqh2uxs3arj" />,
  title: 'Tasks Just... Disappear',
  desc: "Tasks get created, assigned, and then nobody follows up. Things just fall through the cracks and you don't find out until it's too late."
}, {
  icon: <AlertTriangle size={28} data-id="element-2005" data-mp="zcct6f6adj5h" />,
  title: 'Client Bottlenecks',
  desc: 'Your client was supposed to send content 3 days ago and nobody reminded them. Now the whole project is delayed.'
}, {
  icon: <Clock size={28} data-id="element-2006" data-mp="6nxq8r0firai" />,
  title: 'Constant Chasing',
  desc: "You're exhausted from constantly asking 'Did this get done?' and 'Where are we on this?' You're the bottleneck for everything."
}];
const pmLogos = [{
  name: 'ClickUp',
  domain: 'clickup.com'
}, {
  name: 'Basecamp',
  domain: 'basecamp.com'
}, {
  name: 'Teamwork',
  domain: 'teamwork.com'
}, {
  name: 'Monday.com',
  domain: 'monday.com'
}, {
  name: 'Asana',
  domain: 'asana.com'
}, {
  name: 'Trello',
  domain: 'trello.com'
}, {
  name: 'Notion',
  domain: 'notion.so'
}, {
  name: 'Jira',
  domain: 'atlassian.com'
}, {
  name: 'Time Doctor',
  domain: 'timedoctor.com'
}, {
  name: 'Desklog',
  domain: 'desklog.io'
}];
const dartBenefits = ['AI-native project management', 'Unlimited users & workspaces', 'Built-in automation & workflows', 'Clean, lightning-fast interface', 'Custom views (Kanban, List, Calendar)', 'Seamless MR & PAM integration'];
const features = [{
  icon: <ListTodo size={22} data-id="element-2007" data-mp="34fmpnyb4alqi" />,
  title: 'Task Creation',
  desc: 'Tell MR what needs to happen and tasks are created, formatted, and assigned — automatically. No more typing everything out.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Users size={22} data-id="element-2008" data-mp="3ebprf3224icx" />,
  title: 'Smart Assignment',
  desc: 'MR knows who on your team handles what. Tasks go to the right person based on their role and workload.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Clock size={22} data-id="element-2009" data-mp="2lmiq5wb7xenz" />,
  title: 'Deadline Management',
  desc: 'Every task has a deadline. PAM watches them all and warns you before anything runs late.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <MessageSquare size={22} data-id="element-2010" data-mp="s5waxm637ymg" />,
  title: 'Client Follow-Up',
  desc: "Waiting on a client for content, approval, or feedback? PAM reaches out with a friendly reminder so you don't have to nag.",
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Bell size={22} data-id="element-2011" data-mp="1jydhka6n1i6j" />,
  title: 'Team Follow-Up',
  desc: "PAM checks in with your team on their tasks — did you finish this? Do you need help? — so you don't have to micromanage.",
  color: 'bg-teal/10 text-teal'
}, {
  icon: <AlertTriangle size={22} data-id="element-2012" data-mp="1ayqfrn9zyacy" />,
  title: 'Blocker Detection',
  desc: 'Something stuck? PAM spots it and escalates before it becomes a bigger problem.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <BarChart size={22} data-id="element-2013" data-mp="4gbpmwmgopa0" />,
  title: 'Status Reports',
  desc: "Get automatic daily or weekly summaries of what got done, what's in progress, and what needs attention.",
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Inbox size={22} data-id="element-2014" data-mp="eyqcvof2nojd" />,
  title: 'Inbox Reminders',
  desc: 'PAM reminds team members to check their project inbox and respond to messages — building good habits automatically.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Bot size={22} data-id="element-2015" data-mp="6050kb0715r1" />,
  title: 'AI Agent Execution',
  desc: 'Need content written, a page built, or data organized? MR triggers specialized AI agents to actually DO the work — not just track it.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Workflow size={22} data-id="element-2016" data-mp="tnwd9fpnbksu" />,
  title: 'Progress Tracking',
  desc: 'See exactly where every project stands at a glance. No more digging through tools to find answers.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <PieChart size={22} data-id="element-2017" data-mp="3efeuuwxud6o9" />,
  title: 'Budget & Time Tracking',
  desc: 'Keep projects from going over budget. MR tracks time and costs so there are no surprises.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Repeat size={22} data-id="element-2018" data-mp="1oyt3ey1p9e6g" />,
  title: 'Recurring Templates',
  desc: 'Do the same type of project often? Set it up once and MR reuses the template every time — same quality, zero setup.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Eye size={22} data-id="element-2019" data-mp="22omttksus6ia" />,
  title: 'Client Visibility',
  desc: 'Give clients a window into progress without them bugging you for updates. They see what they need to see.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <ShieldCheck size={22} data-id="element-2020" data-mp="2cmax4z4iymc2" />,
  title: 'Quality Checks',
  desc: 'Before marking anything complete, PAM verifies the work meets your standards. No more accepting half-done tasks.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Network size={22} data-id="element-2021" data-mp="2lmz5peb5fuhk" />,
  title: 'Cross-Team Coordination',
  desc: 'When multiple teams or departments are involved, PAM keeps everyone aligned and moving in the same direction.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <CheckSquare size={22} data-id="element-2022" data-mp="1ukdowj5pb5ze" />,
  title: 'Habit Building',
  desc: 'PAM gently trains your team to update tasks, check inboxes, and communicate — building the habits that make projects run smoothly.',
  color: 'bg-purple/10 text-purple'
}];
const withoutMr = ['Tasks disappear and deadlines are missed', 'Clients are forgotten and projects stall', 'You spend hours chasing people for updates', 'You are the bottleneck for every decision', 'Constant stress and late-night worrying', 'Paying a project manager who needs managing'];
const withMr = ['Every task tracked and followed up on automatically', 'Clients gently reminded before things are late', 'Clean status reports delivered to you automatically', 'Projects move forward without your constant input', 'Peace of mind knowing nothing falls through the cracks', 'A flawless AI project manager that never sleeps'];
export function ProjectManager() {
  return <div className="w-full bg-white" data-id="element-2023" data-mp="2rbz5w14wwtbe">
      <Nav data-id="element-2024" data-mp="2r8tg0joglnem" />

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative bg-[#0F1D3A] pt-44 pb-24 px-6 overflow-hidden" data-id="element-2025" data-mp="3awz9dx2s2ms3">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple/15 rounded-full blur-[120px] pointer-events-none" data-id="element-2026" data-mp="3fu7uo6j836xk" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-teal/10 rounded-full blur-[100px] pointer-events-none" data-id="element-2027" data-mp="31xucswas13ik" />
        <div className="max-w-5xl mx-auto relative z-10 text-center" data-id="element-2028" data-mp="2s685hb1k7ol3">
          <motion.div initial="hidden" animate="visible" variants={stagger} data-id="element-2029" data-mp="2s3vd03y6dvts">
            <motion.span variants={fadeUp} className="inline-block text-teal text-sm font-bold tracking-widest uppercase mb-4" data-id="element-2030" data-mp="2wy9d2jns8r4s">
              Self-Managing Projects
            </motion.span>
            <motion.h1 variants={fadeUp} className="font-poppins font-extrabold text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6" data-id="element-2031" data-mp="2n4iqjkb7uqrq">
              Your Projects. <br className="hidden md:block" data-id="element-2032" data-mp="2e800qgya7izd" />
              <span className="text-gradient" data-id="element-2033" data-mp="2tn5fqtc2729z">Managing Themselves.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-2034" data-mp="2oq584dzk9rgr">
              No more chasing your team, wondering if tasks got done, or staying
              up at night worrying about deadlines. MR and PAM handle every
              detail — so you don't have to.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center mb-12" data-id="element-2035" data-mp="25yccscx0ru5v">
              <Link to="/contact" className="btn-primary shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow" data-id="element-2036" data-mp="20xgecevft07u">
                Talk to MR
              </Link>
              <Link to="/how-it-works" className="btn-ghost hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] transition-shadow" data-id="element-2037" data-mp="1n0t4jbidyfki">
                See How It Works
              </Link>
            </motion.div>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-6" data-id="element-2038" data-mp="7in3tehczubo">
              {[{
              value: '100%',
              label: 'Every Task Tracked'
            }, {
              value: '100%',
              label: 'Every Deadline Met'
            }, {
              value: '100%',
              label: 'Every Person Accountable'
            }].map(stat => <div key={stat.label} className="bg-white/5 border border-white/10 rounded-xl px-6 py-3 text-center" data-id="element-2039" data-mp="3toabiiao425m">
                  <div className="text-2xl font-bold text-white" data-id="element-2040" data-mp="317g0yt9enrht">
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-400 font-medium" data-id="element-2041" data-mp="3aztx854f5ls0">
                    {stat.label}
                  </div>
                </div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ PAIN SECTION ═══════════════ */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-2042" data-mp="3oz3gxeru2y0q">
        <div className="max-w-6xl mx-auto" data-id="element-2043" data-mp="3owqxsyacahm3">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2044" data-mp="2rbuo0ypi6mop">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2045" data-mp="35zz8a747f69l">
              Sound Familiar?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2046" data-mp="2djermgr3xmwr">
              If you're exhausted from constantly checking if things got done,
              you're not alone.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid md:grid-cols-2 gap-6" data-id="element-2047" data-mp="1w1zfy4svug2b">
            {painPoints.map(p => <motion.div key={p.title} variants={fadeUp} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:border-purple/40 hover:shadow-md transition-all duration-300" data-id="element-2048" data-mp="1iaum5jc74hzw">
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center text-red-500 mb-4" data-id="element-2049" data-mp="18e82gxvlvwzg">
                  {p.icon}
                </div>
                <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-2" data-id="element-2050" data-mp="zcrwd2btqh11">
                  {p.title}
                </h3>
                <p className="text-gray-500 leading-relaxed" data-id="element-2051" data-mp="ospslchd68x4">{p.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ YOUR MEETINGS ALREADY DO THE WORK ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2052" data-mp="koi2bav9ckws">
        <div className="max-w-5xl mx-auto" data-id="element-2053" data-mp="kp9yamsroknl">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={fadeUp} className="relative bg-gradient-to-br from-[#0F1D3A] to-[#1A447A] rounded-2xl p-10 md:p-14 overflow-hidden" data-id="element-2054" data-mp="18e2n7zaz1s8p">
            <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-purple/20 rounded-full blur-[100px] pointer-events-none" data-id="element-2055" data-mp="13mb2i5ehygqh" />
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-10" data-id="element-2056" data-mp="1w46oi00otfwj">
              <div className="flex-1" data-id="element-2057" data-mp="1w5zzfric2mac">
                <div className="inline-flex items-center gap-2 bg-teal/20 text-teal px-4 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-2058" data-mp="25i730n9z3ahv">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2059" data-mp="21h53s6g22yfw">
                    <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" data-id="element-2060" data-mp="1ikc9q7z2dtki" />
                    <rect x="2" y="6" width="14" height="12" rx="2" data-id="element-2061" data-mp="1wg7lzuyyk6ex" />
                  </svg>
                  Already Built Into Your Day
                </div>
                <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-6" data-id="element-2062" data-mp="37wee68guqtzs">
                  Your Meetings Already Do The Work.
                </h2>
                <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-2063" data-mp="1ch92d4cs09xn">
                  When you hop on a Zoom call or Google Meet, that recording
                  goes straight to MR's brain. Tasks, action items, and next
                  steps are created automatically — right from the conversation
                  you were already having.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-2064" data-mp="1qexjc13q2wxf">
                  No repeating yourself. No sitting down after the call to type
                  everything out. No scheduling another meeting just to make
                  sure you captured it all.{' '}
                  <strong className="text-white" data-id="element-2065" data-mp="256g7im6qjxrv">
                    MR was listening the whole time.
                  </strong>
                </p>
                <p className="text-gray-400 text-base italic" data-id="element-2066" data-mp="2e6wazvnhb4wq">
                  This is how you get your time back — by leveraging the things
                  you're already doing.
                </p>
              </div>
              <div className="w-full md:w-1/3 flex flex-col gap-4" data-id="element-2067" data-mp="7im344ix5n1e">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10" data-id="element-2068" data-mp="2j9b75d6xpry">
                  <div className="text-xs text-teal font-bold mb-1" data-id="element-2069" data-mp="3ums9qzpnpn51">
                    📹 Zoom Call Ends
                  </div>
                  <div className="text-sm text-gray-300" data-id="element-2070" data-mp="3pp0jsb8kob3h">
                    Recording sent to MR's brain
                  </div>
                </div>
                <div className="flex justify-center" data-id="element-2071" data-mp="qaodwz8vq93q">
                  <ArrowRight size={20} className="text-purple rotate-90" data-id="element-2072" data-mp="m8jo6bljxwmd" />
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10" data-id="element-2073" data-mp="hf8pwyut2o62">
                  <div className="text-xs text-purple font-bold mb-1" data-id="element-2074" data-mp="cgnncss2zseg">
                    🧠 MR Processes
                  </div>
                  <div className="text-sm text-gray-300" data-id="element-2075" data-mp="19yxjcazfobl6">
                    Extracts tasks, deadlines & action items
                  </div>
                </div>
                <div className="flex justify-center" data-id="element-2076" data-mp="1o3o1o71hy0z3">
                  <ArrowRight size={20} className="text-teal rotate-90" data-id="element-2077" data-mp="1t1g2jqk4cxff" />
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10" data-id="element-2078" data-mp="1fa19ss0ethv6">
                  <div className="text-xs text-teal font-bold mb-1" data-id="element-2079" data-mp="1jbmrqrrswmc8">
                    ✅ Tasks Created
                  </div>
                  <div className="text-sm text-gray-300" data-id="element-2080" data-mp="15blr9dfg7gxa">
                    Assigned, scheduled & tracked by PAM
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ MEET PAM ═══════════════ */}
      <section className="py-24 px-6 bg-white" data-id="element-2081" data-mp="10c0kigu1e4a0">
        <div className="max-w-5xl mx-auto" data-id="element-2082" data-mp="10ev4y8d1vp3f">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={fadeUp} className="relative rounded-3xl overflow-hidden shadow-2xl" data-id="element-2083" data-mp="2qoydn8196bml">
            <div className="absolute inset-0 bg-gradient-to-br from-purple/5 via-white to-teal/5" data-id="element-2084" data-mp="f2nr83a4inyo" />
            <div className="absolute inset-0 border-2 border-transparent bg-gradient-to-br from-purple to-teal opacity-20 rounded-3xl" style={{
            maskImage: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            maskComposite: 'exclude'
          }} data-id="element-2085" data-mp="stj3lpjtzn70" />

            <div className="relative z-10 p-10 md:p-16 flex flex-col md:flex-row items-center gap-12" data-id="element-2086" data-mp="xuoxvrt53iin">
              <div className="flex-1 text-center md:text-left" data-id="element-2087" data-mp="1bprcr4fysi7l">
                <div className="inline-flex items-center gap-2 bg-purple/10 text-purple px-4 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-2088" data-mp="16tk619fqv2ol">
                  <Sparkles size={16} data-id="element-2089" data-mp="38kf5qcd2nm0l" /> Meet Your New Project Manager
                </div>
                <h2 className="font-poppins font-bold text-4xl md:text-5xl text-gray-900 mb-6" data-id="element-2090" data-mp="38mrr6zi1geie">
                  Say Hello to <span className="text-gradient" data-id="element-2091" data-mp="2rq2uji2s0yvi">PAM.</span>
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed mb-6" data-id="element-2092" data-mp="2rnyfq53cafgg">
                  PAM (Project Account Manager) is an AI agent that lives inside
                  your project management system. She works alongside MR to make
                  sure absolutely nothing falls through the cracks.
                </p>
                <p className="text-gray-600 text-lg leading-relaxed font-medium" data-id="element-2093" data-mp="28r748hu1h4my">
                  She's the project manager you always wished you had — one that
                  never forgets, never drops the ball, and never needs to be
                  managed.
                </p>
              </div>
              <div className="w-full md:w-1/3 flex flex-col gap-4" data-id="element-2094" data-mp="3p68c3oelt0np">
                <div className="bg-white rounded-xl p-4 shadow-sm border border-purple/10 flex items-start gap-3" data-id="element-2095" data-mp="3p82uow2ekgom">
                  <CheckCircle2 className="text-purple shrink-0 mt-0.5" size={20} data-id="element-2096" data-mp="1ih6n1rh71nlm" />
                  <span className="text-sm text-gray-700 font-medium" data-id="element-2097" data-mp="1djo8nlkeqg68">
                    Stays on top of every task
                  </span>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-teal/10 flex items-start gap-3" data-id="element-2098" data-mp="13t3f4cjlc1u6">
                  <CheckCircle2 className="text-teal shrink-0 mt-0.5" size={20} data-id="element-2099" data-mp="zr8e795knqgx" />
                  <span className="text-sm text-gray-700 font-medium" data-id="element-2100" data-mp="uqkelz3a21b3">
                    Follows up with team members
                  </span>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-purple/10 flex items-start gap-3" data-id="element-2101" data-mp="kur5yqsoiryl">
                  <CheckCircle2 className="text-purple shrink-0 mt-0.5" size={20} data-id="element-2102" data-mp="2k6a1o3oz23qb" />
                  <span className="text-sm text-gray-700 font-medium" data-id="element-2103" data-mp="38ia80avolrys">
                    Reminds clients about deadlines
                  </span>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-teal/10 flex items-start gap-3" data-id="element-2104" data-mp="33jyy605dzhgk">
                  <CheckCircle2 className="text-teal shrink-0 mt-0.5" size={20} data-id="element-2105" data-mp="2yqe0yabmarif" />
                  <span className="text-sm text-gray-700 font-medium" data-id="element-2106" data-mp="2psbsj9pk0wl4">
                    Escalates blockers instantly
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ HOW IT ACTUALLY WORKS ═══════════════ */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-2107" data-mp="2btcd8snxlu1m">
        <div className="max-w-5xl mx-auto" data-id="element-2108" data-mp="e29dxf5ugajw">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-16" data-id="element-2109" data-mp="e17zr1tj77xy">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2110" data-mp="1wx477pv7k987">
              How It Actually Works
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2111" data-mp="25pxotty4nkp8">
              Let's look at a real-world scenario. Here's what happens when you
              need to build a website.
            </motion.p>
          </motion.div>

          <div className="relative" data-id="element-2112" data-mp="1hztuyeaqqzbf">
            {/* Connecting Line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple via-teal to-purple opacity-20 hidden md:block" data-id="element-2113" data-mp="1myy5p9dbd53v" />

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-80px'
          }} variants={stagger} className="space-y-12" data-id="element-2114" data-mp="2teqr085ceosa">
              {/* Step 1 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2115" data-mp="ua8nh9xjcwwz">
                <div className="md:w-1/2 flex justify-end" data-id="element-2116" data-mp="13ax2a6ur71ax">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 w-full md:w-[400px] relative" data-id="element-2117" data-mp="flm0cm1o30v7">
                    <div className="absolute top-6 -right-3 w-6 h-6 bg-white border-t border-r border-gray-100 rotate-45 hidden md:block" data-id="element-2118" data-mp="an19vec7vvjy" />
                    <div className="text-sm font-bold text-purple mb-2" data-id="element-2119" data-mp="pd04j74kcouo">
                      Step 1: You Talk
                    </div>
                    <p className="text-gray-600 text-sm" data-id="element-2120" data-mp="3t0imamfw4kkr">
                      "Hey MR, we need to build the Johnson website by March
                      15th."
                    </p>
                  </div>
                </div>
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-purple text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2121" data-mp="23j4w53uuue8g">
                  1
                </div>
                <div className="md:w-1/2" data-id="element-2122" data-mp="1uglycy5xert2" />
              </motion.div>

              {/* Step 2 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2123" data-mp="1yi7in4ksi8da">
                <div className="md:w-1/2" data-id="element-2124" data-mp="2r7wjnhxi28ew" />
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-teal text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2125" data-mp="2r6v5v5p3n7th">
                  2
                </div>
                <div className="md:w-1/2 flex justify-start" data-id="element-2126" data-mp="11trrj4217qy2">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 w-full md:w-[400px] relative" data-id="element-2127" data-mp="11wdwqx1g9ud4">
                    <div className="absolute top-6 -left-3 w-6 h-6 bg-white border-b border-l border-gray-100 rotate-45 hidden md:block" data-id="element-2128" data-mp="1m2hltr9mhfij" />
                    <div className="text-sm font-bold text-teal mb-2" data-id="element-2129" data-mp="1hb9b8rwk3o3s">
                      Step 2: MR Creates
                    </div>
                    <p className="text-gray-600 text-sm" data-id="element-2130" data-mp="1calmkcvsvjcq">
                      MR creates the full project — tasks, deadlines,
                      assignments — automatically.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Step 3 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2131" data-mp="1w0xbdpt04gpx">
                <div className="md:w-1/2 flex justify-end" data-id="element-2132" data-mp="1r1cah9u9tyak">
                  <div className="bg-purple/5 p-6 rounded-xl shadow-sm border border-purple/20 w-full md:w-[400px] relative" data-id="element-2133" data-mp="kmbej458xg3z">
                    <div className="absolute top-6 -right-3 w-6 h-6 bg-[#f9f5ff] border-t border-r border-purple/20 rotate-45 hidden md:block" data-id="element-2134" data-mp="flw2cfwpaenl" />
                    <div className="text-sm font-bold text-purple mb-2 flex items-center gap-2" data-id="element-2135" data-mp="2r87qkk7axhqp">
                      <Sparkles size={14} data-id="element-2136" data-mp="2w6r90amosbeb" /> Step 3: PAM Follows Up
                    </div>
                    <p className="text-gray-700 text-sm italic" data-id="element-2137" data-mp="308w29898360f">
                      "Hey Stacey, just a heads up — the homepage content is due
                      Thursday. If it's not ready, I'll push the deadline back a
                      week so we stay on track."
                    </p>
                  </div>
                </div>
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-purple text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2138" data-mp="3jygaaghhka7e">
                  3
                </div>
                <div className="md:w-1/2" data-id="element-2139" data-mp="1pmrqo10b7jgo" />
              </motion.div>

              {/* Step 4 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2140" data-mp="1tocyh7xzl7k0">
                <div className="md:w-1/2" data-id="element-2141" data-mp="2rz4ruinc11av" />
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-teal text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2142" data-mp="2s07jisshb7ru">
                  4
                </div>
                <div className="md:w-1/2 flex justify-start" data-id="element-2143" data-mp="3fvimsx320olm">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 w-full md:w-[400px] relative" data-id="element-2144" data-mp="3ftpb33dl3e6r">
                    <div className="absolute top-6 -left-3 w-6 h-6 bg-white border-b border-l border-gray-100 rotate-45 hidden md:block" data-id="element-2145" data-mp="3pllmfg4ol0sx" />
                    <div className="text-sm font-bold text-teal mb-2" data-id="element-2146" data-mp="3ko39zfrgg3g6">
                      Step 4: PAM Verifies
                    </div>
                    <p className="text-gray-600 text-sm" data-id="element-2147" data-mp="2et3k803xx0q">
                      Tasks get completed, PAM verifies quality, updates the
                      project, and keeps everyone in the loop.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Step 5 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2148" data-mp="83phwp1yi8bt">
                <div className="md:w-1/2 flex justify-end" data-id="element-2149" data-mp="c6uiun6e21yc">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 w-full md:w-[400px] relative" data-id="element-2150" data-mp="3uhpohxuzwfhz">
                    <div className="absolute top-6 -right-3 w-6 h-6 bg-white border-t border-r border-gray-100 rotate-45 hidden md:block" data-id="element-2151" data-mp="35ny21wpi4za" />
                    <div className="text-sm font-bold text-purple mb-2" data-id="element-2152" data-mp="3pjnx8pnf19vf">
                      Step 5: You Relax
                    </div>
                    <p className="text-gray-600 text-sm" data-id="element-2153" data-mp="3bky05a0lja72">
                      You get a clean status report without asking for one. The
                      project is done.
                    </p>
                  </div>
                </div>
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-purple text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2154" data-mp="3vymwtalxpj48">
                  5
                </div>
                <div className="md:w-1/2" data-id="element-2155" data-mp="3i1zrokjtvmn8" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════ WORKS WITH ANY PM TOOL ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2156" data-mp="38adfolzo1jjf">
        <div className="max-w-5xl mx-auto" data-id="element-2157" data-mp="389ljpa25pjsm">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2158" data-mp="33ddbkgmdmquy">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-teal/10 text-teal px-4 py-1.5 rounded-full text-sm font-semibold mb-4" data-id="element-2159" data-mp="2pcsr7g0tcmzc">
              <Plug size={16} data-id="element-2160" data-mp="19tcpakxn0dfn" /> Works With Any PM Tool
            </motion.div>
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2161" data-mp="19vfpfxsi7my3">
              We Integrate With What You Already Love.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2162" data-mp="m4th05el80nk">
              Already using a project management tool? Keep it. MR and PAM plug
              right in and make it actually work the way it should.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid grid-cols-2 sm:grid-cols-4 gap-6" data-id="element-2163" data-mp="zw6wn89dkedb">
            {pmLogos.map(tool => <motion.div key={tool.name} variants={fadeUp} className="bg-gray-50 rounded-xl p-5 flex flex-col items-center gap-3 border border-gray-200 hover:border-purple/40 hover:shadow-md transition-all duration-300" data-id="element-2164" data-mp="3d8i4yx6qneb4">
                <img src={`https://logo.clearbit.com/${tool.domain}`} alt={tool.name} className="w-10 h-10 rounded-lg object-contain" onError={e => {
              const target = e.target as HTMLImageElement;
              target.src = `https://www.google.com/s2/favicons?domain=${tool.domain}&sz=128`;
            }} data-id="element-2165" data-mp="3976fcrnv6t2x" />
                <span className="text-sm font-semibold text-gray-700" data-id="element-2166" data-mp="2bl91x8ocddxu">
                  {tool.name}
                </span>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ OR GET DART AI INCLUDED ═══════════════ */}
      <section className="py-20 px-6 bg-[#0F1D3A]" data-id="element-2167" data-mp="1x0mudwesz3yt">
        <div className="max-w-5xl mx-auto" data-id="element-2168" data-mp="1s0im2to1tfjo">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} data-id="element-2169" data-mp="1s1k0970d2i5m">
            <motion.div variants={fadeUp} className="text-center mb-12" data-id="element-2170" data-mp="2fx4rktcf7w2g">
              <span className="inline-flex items-center gap-2 bg-purple/10 text-purple px-4 py-1.5 rounded-full text-sm font-semibold mb-4" data-id="element-2171" data-mp="2fvbil7ec4zra">
                <Gift size={16} data-id="element-2172" data-mp="2pnha6g0odf5y" /> Included With Your Complete Package
              </span>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4" data-id="element-2173" data-mp="2knl5sfo2flpv">
                Or Get Your Own Workspace —{' '}
                <span className="text-gradient" data-id="element-2174" data-mp="2yoffd1munsq4">On Us.</span>
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-2" data-id="element-2175" data-mp="2unc28mw6sasl">
                Don't have a PM tool yet? When you go with our complete package,
                you get your own project workspace inside{' '}
                <strong className="text-white" data-id="element-2176" data-mp="36p3jp7qv34nt">Dart AI</strong> — an AI-native
                project management system included at no extra cost.
              </p>
              <p className="text-gray-500 text-sm" data-id="element-2177" data-mp="3gewsy23j77ow">
                Already have a PM tool? That's totally fine — this is a bonus,
                not a requirement.
              </p>
            </motion.div>
            <motion.div variants={fadeUp} className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-10" data-id="element-2178" data-mp="3pea4r1f4o8cs">
              <div className="flex flex-col md:flex-row items-center gap-6 mb-8" data-id="element-2179" data-mp="3ucbrpxfocrtj">
                <div className="flex items-center gap-3" data-id="element-2180" data-mp="373icbft9xj6">
                  <div className="w-12 h-12 rounded-xl bg-purple/20 flex items-center justify-center" data-id="element-2181" data-mp="200ww4zzzfy5v">
                    <Layout size={24} className="text-purple" data-id="element-2182" data-mp="2509lbrebthbm" />
                  </div>
                  <div data-id="element-2183" data-mp="m4dijgnlh47p">
                    <h3 className="text-xl font-poppins font-bold text-white" data-id="element-2184" data-mp="m4x0uyh5pum9">
                      Project Workspace
                    </h3>
                    <span className="text-xs text-gray-400 bg-white/5 px-2 py-0.5 rounded-full" data-id="element-2185" data-mp="dbtg0546mfhl">
                      Powered by Dart AI
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2186" data-mp="1539byprmpekh">
                {dartBenefits.map(b => <div key={b} className="flex items-start gap-3" data-id="element-2187" data-mp="112petm722ayc">
                    <CheckCircle2 size={18} className="text-teal shrink-0 mt-0.5" data-id="element-2188" data-mp="w1rylf5lvqzh" />
                    <span className="text-gray-300 text-sm" data-id="element-2189" data-mp="r2ovi096ruu5">{b}</span>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ EVERYTHING MR & PAM HANDLE ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2190" data-mp="13joeb4dxm5f6">
        <div className="max-w-6xl mx-auto" data-id="element-2191" data-mp="13iwns7z6yxvr">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2192" data-mp="18chklwp1tltd">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2193" data-mp="1n8zyxqsg4lwp">
              Everything MR & PAM Handle
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2194" data-mp="1dbc1uu7db2bd">
              From creating tasks to gently nagging your clients for content —
              they do it all.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-2195" data-mp="6v024oowdcdo">
            {features.map(f => <motion.div key={f.title} variants={fadeUp} className="bg-gray-50 rounded-xl p-5 border border-teal/40 hover:border-purple hover:shadow-md transition-all duration-300 group" data-id="element-2196" data-mp="8acjhdw0nz0h">
                <div className={`w-10 h-10 rounded-xl ${f.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`} data-id="element-2197" data-mp="37qrcn25h9g19">
                  {f.icon}
                </div>
                <h3 className="font-poppins font-semibold text-gray-900 mb-2" data-id="element-2198" data-mp="3gqed9lqduht9">
                  {f.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed" data-id="element-2199" data-mp="2t01vzk98z8q0">
                  {f.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ BEFORE & AFTER ═══════════════ */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-2200" data-mp="wxoq0mm0t9sj">
        <div className="max-w-5xl mx-auto" data-id="element-2201" data-mp="wwdlulony00w">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2202" data-mp="11p6uhkn95si9">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2203" data-mp="dzun8qbhjuil">
              The Difference Is Night and Day
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid md:grid-cols-2 gap-8" data-id="element-2204" data-mp="n1bxhs27p1zd">
            {/* Without MR */}
            <motion.div variants={fadeUp} className="bg-red-50 border border-red-200 rounded-2xl p-8" data-id="element-2205" data-mp="3ix8hi32xii2f">
              <h3 className="font-poppins font-bold text-xl text-red-700 mb-6 flex items-center gap-2" data-id="element-2206" data-mp="3a56i884lj7tq">
                <XCircle size={24} data-id="element-2207" data-mp="356lehyq4y60j" /> Without MR & PAM
              </h3>
              <ul className="space-y-4" data-id="element-2208" data-mp="3548yvs7uo7qn">
                {withoutMr.map(item => <li key={item} className="flex items-start gap-3 text-red-700/80" data-id="element-2209" data-mp="308jzppzqssuv">
                    <XCircle size={16} className="shrink-0 mt-1 text-red-400" data-id="element-2210" data-mp="2r629z1kvpi34" />
                    <span className="text-sm leading-relaxed" data-id="element-2211" data-mp="2m7hbb6l9z6gs">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
            {/* With MR */}
            <motion.div variants={fadeUp} className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8" data-id="element-2212" data-mp="pcuxs10kbglg">
              <h3 className="font-poppins font-bold text-xl text-emerald-700 mb-6 flex items-center gap-2" data-id="element-2213" data-mp="3ph7iu27wrv6a">
                <CheckCircle2 size={24} data-id="element-2214" data-mp="3udxxqm7z8kq4" /> With MR & PAM
              </h3>
              <ul className="space-y-4" data-id="element-2215" data-mp="3fquwq5kg3ty1">
                {withMr.map(item => <li key={item} className="flex items-start gap-3 text-emerald-700/80" data-id="element-2216" data-mp="3kp6aushrwqm2">
                    <CheckCircle2 size={16} className="shrink-0 mt-1 text-emerald-500" data-id="element-2217" data-mp="36o3m27ze927g" />
                    <span className="text-sm leading-relaxed" data-id="element-2218" data-mp="2wrqvhiz5b4gx">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ GET YOUR TIME BACK ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2219" data-mp="uyb24ebobbi9">
        <div className="max-w-5xl mx-auto text-center" data-id="element-2220" data-mp="zy5rgdfsvt17">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} data-id="element-2221" data-mp="2kh98sim6v09f">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-2222" data-mp="2u7krjbaj7e0d">
              What If You Could Get Your Time Back?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-2223" data-mp="33663gnlhzus6">
              Think about all the hours you spend chasing updates, reminding
              people, checking if things got done, and worrying about what fell
              through the cracks. Now imagine all of that handled —
              automatically, consistently, every single day.
            </motion.p>
            <motion.div variants={fadeUp} className="grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto mb-10" data-id="element-2224" data-mp="1dtcce1s3c6yr">
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-2225" data-mp="2ete06sck2tmz">
                <div className="text-4xl font-bold text-purple mb-2" data-id="element-2226" data-mp="29vcfy43e8o9x">10+</div>
                <div className="text-sm text-gray-600" data-id="element-2227" data-mp="2orle12bjsvkr">
                  Hours saved per week on follow-ups and status checks
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-2228" data-mp="2xs02gajt6qsh">
                <div className="text-4xl font-bold text-teal mb-2" data-id="element-2229" data-mp="2xpx5t7o5hzes">0</div>
                <div className="text-sm text-gray-600" data-id="element-2230" data-mp="2ssdofyw5p6im">
                  Tasks that fall through the cracks
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-2231" data-mp="32j00jj4nzewk">
                <div className="text-4xl font-bold text-purple mb-2" data-id="element-2232" data-mp="32l2q621wn60r">100%</div>
                <div className="text-sm text-gray-600" data-id="element-2233" data-mp="3girbfazvwf6c">
                  Of your focus back on growing your business
                </div>
              </div>
            </motion.div>
            <motion.p variants={fadeUp} className="text-gray-500 text-base max-w-2xl mx-auto" data-id="element-2234" data-mp="3v5py6snsqleq">
              MR and PAM aren't here to replace anyone — they're here to handle
              the tedious, time-consuming details that eat up your day. The
              stuff that keeps you up at night. So you can focus on the work
              that actually moves the needle.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ FINAL CTA ═══════════════ */}
      <section className="py-24 px-6 bg-[#0F1D3A] text-center" data-id="element-2235" data-mp="33e0iv39t8df4">
        <div className="max-w-3xl mx-auto" data-id="element-2236" data-mp="33fv6wqgdolnf">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} data-id="element-2237" data-mp="37f41fxj8snk1">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-white mb-6" data-id="element-2238" data-mp="2ydw4bisgpsrj">
              Ready to Stop Being Everyone's Project Manager?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-400 text-lg mb-10 max-w-xl mx-auto" data-id="element-2239" data-mp="z5w8axpiia9q">
              Let MR and PAM handle the details, the follow-ups, and the
              deadlines — so you can focus on growing your business.
            </motion.p>
            <motion.div variants={fadeUp} data-id="element-2240" data-mp="1mv8pajr2zmml">
              <Link to="/contact" className="btn-primary shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow text-lg px-10 py-4" data-id="element-2241" data-mp="1ivq0hcw52reh">
                Talk to MR
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-2242" data-mp="2fn8zdght0ke" />
    </div>;
}